// The per-tick blackboard every bot routine reads (one object per BotManager, refilled for each
// bot), the telemetry the harness and the debug viewer read, and small deterministic helpers.
// Determinism (§18.4): only arithmetic, sqrt, hypot, sin, cos and atan2; no pow/exp/log.
import { MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { createRoundClock, type RoundClock } from '../maps/clock';
import { rand, type RngHolder } from '../rng';
import type { Bean, InputFrame, World } from '../types';
import type { BotContext } from './botTypes';
import type { Brain } from './brain';
import type { HazardForecast } from './hazards';
import { createSeen, type Perception, type Seen } from './perception';
import type { SafetyField } from './safetyField';
import { RoutePlanner } from './routes';

/** Counters the harness reports and asserts on (§30.3, §29.4). Cumulative until cleared. */
export interface BotTelemetry {
  thinks: number;
  shoves: number;
  blunders: number;
  grabs: number;
  throws: number;
  hazardJumps: number;
  jumpDodges: number;
  sidesteps: number;
  clashAttempts: number;
  anticipations: number;
  teeterSaves: number;
  teeterFreezes: number;
  ledgeClimbs: number;
  /** Ledge climbs that waited for an approaching stomper (§10.2 RECOVER mind game). */
  mindGames: number;
  /** Ticks spent getting away from the rim right after a climb (the ledge is used up for 5 s). */
  climbRetreatTicks: number;
  stompAttempts: number;
  baits: number;
  /** Item presses per ItemKind. */
  itemPresses: number[];
  honks: number;
  emotes: number;
  dances: number;
  /** Ticks spent per goal (index = Goal). */
  goalTicks: number[];
  /** Ticks a bean on its feet spent in edge RECOVER (§10.2). */
  recoverTicks: number;
  /** Ticks after which more than TUNE.bot.maxPerTarget brains targeted one bean (must stay 0). */
  capViolations: number;
  /** Bombs picked up to throw back. */
  bombGrabs: number;
  /** ATTACK commits by opening: free, punish, away, edge, dizzy, impatient (in that order). */
  commits: number[];
  /** Chaos Goblin "shove anyone within 2.2 m" shoves. */
  chaosShoves: number;
  /** Ticks spent stepping out from under a ghost pebble. */
  pebbleDodges: number;
  /** Cake island hops taken. */
  islandHops: number;
}

export function createTelemetry(): BotTelemetry {
  return {
    thinks: 0, shoves: 0, blunders: 0, grabs: 0, throws: 0, hazardJumps: 0, jumpDodges: 0, sidesteps: 0,
    clashAttempts: 0, anticipations: 0, teeterSaves: 0, teeterFreezes: 0, ledgeClimbs: 0, mindGames: 0, climbRetreatTicks: 0,
    stompAttempts: 0, baits: 0, itemPresses: new Array<number>(7).fill(0), honks: 0, emotes: 0, dances: 0,
    goalTicks: new Array<number>(16).fill(0),
    recoverTicks: 0, capViolations: 0, bombGrabs: 0, commits: new Array<number>(6).fill(0), chaosShoves: 0, pebbleDodges: 0, islandHops: 0,
  };
}

export interface Ctx {
  w: World;
  bc: BotContext;
  field: SafetyField;
  hz: HazardForecast;
  per: Perception;
  /** Island route planner (shared scratch). */
  routes: RoutePlanner;
  /** Bots targeting each bean (§10.2 hard rule: at most TUNE.bot.maxPerTarget). */
  counts: number[];
  tele: BotTelemetry;
  clock: RoundClock;
  /** Other beans as the current bot perceives them (its own slot is current). */
  seen: Seen[];
  self: Bean;
  brain: Brain;
  /** The current bot's perception delay in ticks. */
  delay: number;
  /** The current bot's edge margin, capped by the ground that exists (Sudden Death, Cake islands). */
  margin: number;
  frame: InputFrame;
  /** Humans by slot (from BotContext.humanSlots). */
  human: boolean[];
  /** Highest match score with at least one point, strictly (NO_SLOT on ties or none). */
  leader: number;
  /** Centroid of the beans with a body (map center if none). */
  cx: number;
  cy: number;
  /** Beans with a body. */
  alive: number;
  /** Round tick of the last KO (0 at round start). */
  lastKoTick: number;
  /** Round tick at which only two beans were left (−1: more remain). */
  duelSince: number;
  /** Map anchors (Cake tiles) claimed by brains this tick, by tile index. */
  anchorClaims: number[];
}

export function createCtx(field: SafetyField, hz: HazardForecast, per: Perception, counts: number[], tele: BotTelemetry): Ctx {
  return {
    w: null as unknown as World,
    bc: null as unknown as BotContext,
    field, hz, per, counts, tele, routes: new RoutePlanner(),
    clock: createRoundClock(),
    seen: Array.from({ length: MAX_BEANS }, createSeen),
    self: null as unknown as Bean,
    brain: null as unknown as Brain,
    delay: 0,
    margin: 1,
    frame: null as unknown as InputFrame,
    human: new Array<boolean>(MAX_BEANS).fill(false),
    leader: NO_SLOT,
    cx: 0, cy: 0, alive: 0,
    duelSince: -1,
    lastKoTick: 0,
    anchorClaims: new Array<number>(64).fill(0),
  };
}

// ---- deterministic helpers ------------------------------------------------------------------

/** Approximately standard-normal draw (Irwin–Hall of 3 uniforms, variance 1). */
export function gauss(h: RngHolder): number {
  return (rand(h) + rand(h) + rand(h) - 1.5) * 2;
}

/** x^0.7 on [0, 1] by table (no Math.pow in decisions, §18.4). */
const POW07 = [
  0, 0.0884, 0.1436, 0.1907, 0.2333, 0.2727, 0.3098, 0.3451, 0.3789, 0.4115,
  0.443, 0.4736, 0.5033, 0.5323, 0.5606, 0.5884, 0.6156, 0.6423, 0.6685, 0.6943,
  0.7196, 0.7446, 0.7693, 0.7936, 0.8176, 0.8413, 0.8647, 0.8879, 0.9108, 0.9334,
  0.9558, 0.978, 1,
];
export function pow07(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const f = x * 32;
  const i = Math.floor(f);
  return POW07[i] + (POW07[i + 1] - POW07[i]) * (f - i);
}


/** The bean stands (or moves) on its own feet and can be lined up for a shove. */
export function grounded(b: Bean): boolean {
  return b.z - b.groundZ <= TUNE.bean.airborneZ;
}

